import type { ProductData } from "../components/ProductInfo/AIImportModal/types";
import Env from "../utils/env";
import { getAuthToken, getUri } from "../utils/fetch";
import { SSEStream } from "../utils/sse-stream";

export interface AIRateLimitResponse {
	limit: number;
	remaining: number;
	resetAt: number;
}

export const getAIRateLimit = (abortSignal?: AbortSignal): Promise<AIRateLimitResponse> => {
	const params: RequestInit = {};
	if (abortSignal) {
		Object.assign(params, { signal: abortSignal });
	}
	const uri = Env.connectAIAPIURL.clone().setPath(`${Env.connectAIAPIPrefix}/metadata/rate-limit`);
	return getUri<AIRateLimitResponse>(uri, params);
};

export interface ExtractProductError {
	status: number;
	message: string;
}

export class AIExtractError extends Error {
	status: number;
	constructor(message: string, status: number) {
		super(message);
		this.name = "AIExtractError";
		this.status = status;
	}
}

export interface AIExtractResult {
	data: ProductData;
	requestId: string;
}

export const createExtractStream = (urls: string[], signal?: AbortSignal): SSEStream => {
	const uri = Env.connectAIAPIURL.clone().setPath(`${Env.connectAIAPIPrefix}/product/extract`);

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		Accept: "text/event-stream",
	};
	const token = getAuthToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	return new SSEStream(
		(sig) =>
			fetch(uri.toString(), {
				method: "POST",
				headers,
				body: JSON.stringify({ website_url: urls }),
				signal: sig,
			}),
		{ signal },
	);
};

export const extractProductFields = async (stream: SSEStream): Promise<AIExtractResult> => {
	for await (const event of stream) {
		if (event.event === "complete") {
			const parsed = JSON.parse(event.data) as { success: boolean; requestId: string; data: ProductData };
			return { data: parsed.data, requestId: parsed.requestId };
		}

		if (event.event === "error") {
			const errorData = JSON.parse(event.data) as ExtractProductError;
			throw new AIExtractError(errorData.message, errorData.status);
		}
	}

	throw new Error("Stream ended without a complete event");
};
